// Presentation helpers for the Shadow Billing (Detail) tables (WEB-6568).

/**
 * Strip the part of a tenant name that duplicates the property name, returning
 * only the unique portion. Matches the property name as a substring — NOT a
 * naive "-" split, which fails for tenants whose own name contains a dash — and
 * returns the text that precedes it, trimmed of any trailing separator.
 *
 * When the property name is unavailable or not embedded, names with two or more
 * ` - ` separators (`{tenant} - {property} - {location}`) fall back to the
 * segment before the first separator.
 *
 *   displayTenantName(
 *     'Planet Fitness - Chico Marketplace - Chico, CA',
 *     'Chico Marketplace - Chico, CA'
 *   ) === 'Planet Fitness'
 *
 * Falls back to the original name when the property name is absent, not found,
 * or found at the very start (where stripping would empty the name).
 */
const TENANT_NAME_SEP = ' - '

const truncateAtSecondSeparator = (name: string): string => {
  const first = name.indexOf(TENANT_NAME_SEP)
  if (first === -1) return name
  const second = name.indexOf(TENANT_NAME_SEP, first + TENANT_NAME_SEP.length)
  if (second === -1) return name
  return name.slice(0, first).trim()
}

export const displayTenantName = (tenantName: string | null | undefined, propertyName?: string | null): string => {
  const name = (tenantName ?? '').trim()
  if (name === '') return name
  const norm = (s: string): string => s.replace(/\s+/g, ' ').trim()
  const t = norm(name)
  if (propertyName != null) {
    const p = norm(propertyName)
    if (p !== '') {
      const idx = t.toLowerCase().lastIndexOf(p.toLowerCase())
      if (idx > 0) {
        const prefix = t.slice(0, idx).replace(/\s*[-–—,|/]+\s*$/, '').trim()
        if (prefix !== '') return prefix
      }
    }
  }
  return truncateAtSecondSeparator(t)
}

/**
 * Split a property name on the first ` - ` separator into site name and location.
 *   '2200 Jerrold Ave - San Francisco, CA' → { name: '2200 Jerrold Ave', location: 'San Francisco, CA' }
 * Returns the full name with a null location when no separator is present.
 */
export const splitPropertyNameLocation = (
  propertyName: string
): { name: string; location: string | null } => {
  const trimmed = propertyName.trim()
  if (trimmed === '') return { name: '', location: null }
  const match = trimmed.match(/^(.+?)\s+-\s+(.+)$/)
  if (match == null) return { name: trimmed, location: null }
  const name = match[1].trim()
  const location = match[2].trim()
  if (name === '' || location === '') return { name: trimmed, location: null }
  return { name, location }
}

/** Property + location for display; API location wins when present. */
export const resolvePropertyDisplay = (
  propertyName: string,
  apiLocation?: string | null
): { name: string; location: string | null } => {
  const { name, location: parsedLocation } = splitPropertyNameLocation(propertyName)
  return {
    name: parsedLocation != null ? name : propertyName,
    location: apiLocation != null && apiLocation !== '' ? apiLocation : parsedLocation
  }
}

/**
 * Variance category meanings, surfaced as pill/filter tooltips (WEB-6568).
 * Variance = the utility-billed solar charge minus SABER's shadow bill for the
 * same invoice; the category names what most of that difference comes from.
 */
export const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  KWH_DRIVEN:
    "KWH-driven variance: most of the gap between the utility-billed solar charge and SABER's shadow bill comes from the kWh (volume), not the rate. Both applied a similar effective $/kWh, but they disagree on how many kWh the tenant was charged for — usually because metered usage, the tenant's allocated solar share, or an over-allocation cap differs. Investigate the meter reads and the allocation before the tariff.",
  RATE_DRIVEN:
    "Rate-driven variance: most of the gap comes from the effective $/kWh rate (tariff, TOU mix, or rate lookup), not the volume. The utility and SABER agree on the kWh but priced them differently.",
  DISCOUNT_DRIVEN:
    "Discount-driven variance: most of the gap comes from the corp-discount term that turns the gross solar charge into the net charge. The utility and SABER largely agree on kWh and base rate, but the applied discount — or the gross-vs-net billing basis — differs. Check the tenant's contracted discount and billing mode.",
  ALLOCATION_DRIVEN:
    "Allocation-driven variance: SABER allocated the tenant more solar than the utility ultimately billed for — typically an over-allocation the utility capped at the meter's actual consumption (WEB-6591). The gap is in the allocated share, not the rate; review the tenant's allocation percent and the over-allocation cap.",
  MIXED:
    'Mixed variance: the difference is split between kWh (volume) and rate (price) — no single factor dominates, so review both the allocation and the rate.',
  IMMATERIAL:
    'Immaterial variance: the difference between the utility bill and SABER is below the materiality threshold — small enough that it is not attributed to a specific driver and generally needs no action.'
}

/** Tooltip text for a variance-category pill; generic fallback for unknown codes. */
export const categoryTooltip = (category: string): string =>
  CATEGORY_DESCRIPTIONS[category.toUpperCase()] ?? `Variance category: ${category.replaceAll('_', ' ')}`

/**
 * Friendly label for a variance-category code, shown on the combined "Variance
 * Driver" pill (WEB-6568 §2f). Falls back to title-cased words for unknown codes.
 */
export const CATEGORY_LABELS: Record<string, string> = {
  KWH_DRIVEN: 'kWh-Driven',
  RATE_DRIVEN: 'Rate-Driven',
  DISCOUNT_DRIVEN: 'Discount-Driven',
  ALLOCATION_DRIVEN: 'Allocation-Driven',
  MIXED: 'Mixed',
  IMMATERIAL: 'Immaterial'
}

/** Full variance-category filter order (§5); segments absent from the current rows are greyed out. */
export const VARIANCE_CATEGORY_ORDER = [
  'KWH_DRIVEN',
  'RATE_DRIVEN',
  'DISCOUNT_DRIVEN',
  'ALLOCATION_DRIVEN',
  'MIXED',
  'IMMATERIAL'
] as const

export const categoryLabel = (category: string): string =>
  CATEGORY_LABELS[category.toUpperCase()] ??
  category.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())

/**
 * Completeness-status meanings + thresholds, surfaced as pill/filter tooltips on
 * the Completeness Check table. Completeness % = billed kWh ÷ allocated kWh for
 * the meter over the selected window; the status buckets that ratio (backend
 * COMPLETENESS_OK_PCT = 98, COMPLETENESS_WARN_PCT = 95).
 */
export const COMPLETENESS_STATUS_DESCRIPTIONS: Record<string, string> = {
  OK: 'OK — billed kWh is at least 98% of allocated kWh for this window. Utility billing has effectively caught up to SABER’s allocation; no action needed.',
  WARN: 'WARN — billed kWh is between 95% and 98% of allocated kWh. A noticeable slice of allocated production has not yet been billed by the utility; usually clears once the next bill lands, but worth watching.',
  CRITICAL:
    'CRITICAL — billed kWh is below 95% of allocated kWh, or there is no completeness data at all. A large share of allocated production is unbilled; check for missing or delayed utility bills.'
}

/** Tooltip text for a completeness-status pill; generic fallback for unknown codes. */
export const completenessStatusTooltip = (status: string): string =>
  COMPLETENESS_STATUS_DESCRIPTIONS[status.toUpperCase()] ?? `Completeness status: ${status}`

export { varianceColorClass } from '../../cellColor'

/**
 * Shadow Billing › TOU Rate Breakdown (normalized) section label tooltip (WEB-6582).
 * Rows aggregate allocated solar kWh and gross $ by season × TOU × tariff bucket;
 * the effective rate normalizes each bucket to $/kWh so buckets are comparable.
 */
export const touBreakdownSectionTooltip =
  'Allocated solar production split by utility season, time-of-use period, and tariff schedule. ' +
  'Effective rate = gross $ ÷ allocated kWh for each bucket (normalized so you can compare $/kWh across buckets). ' +
  '% of Production is each bucket’s share of the table’s total allocated kWh.'

/**
 * Tariff-match meanings for the per-TOU-bucket comparison in the variance drawer.
 * Each bucket's tariff schedule as billed by the utility is checked against the
 * schedule SABER used to price the same bucket.
 */
export const tariffMatchTooltip = (match: boolean): string =>
  match
    ? 'OK — the utility billed this TOU bucket on the same tariff schedule SABER used to price it, so the two rates share a basis and are directly comparable.'
    : 'WARN — the utility billed this TOU bucket on a different tariff schedule than SABER used. The Billing Rate and SABER Rate are being compared across mismatched schedules, which can by itself drive the rate variance — verify the tenant’s effective tariff.'
